import * as S from './styles';
import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { setLearningCenterState } from 'redux/mode/actions';
import { ENTRIES, LearningCenterEntry } from 'components/Modal/LearningCenterModal';
import { LearningCenterVideo } from 'dragonmouse-shared/interfaces';
import Icon, { IconType } from 'components/Icon';
import { useTheme } from 'styled-components';
import { selectUser } from 'redux/db/reducers';
import { unthemed } from 'tokens';
import { trackLearningAction } from 'analytics';

const LearningCenterPanel = () => {
	const dispatch = useDispatch();
	const [isClosing, setIsClosing] = useState(false);
	const theme = useTheme();
	const { panelVisible, user, videoPositions, currentVideo } = useSelector(
		(r: RootReducer) => ({
			panelVisible: r.mode.learningCenterState.panelVisible,
			user: selectUser(r.db),
			videoPositions: r.mode.learningCenterState.videoPositions,
			currentVideo: r.mode.learningCenterState.currentVideo,
		}),
		shallowEqual
	);

	const handleClose = useCallback(() => {
		trackLearningAction('video_side_panel', 'close');
		setIsClosing(true);
		// Wait for animation to complete before hiding
		setTimeout(() => {
			dispatch(setLearningCenterState({ panelVisible: false }));
		}, 150);
	}, [dispatch]);

	useEffect(() => {
		if (panelVisible) {
			setIsClosing(false);
		}
	}, [panelVisible]);

	// Handle Escape key to close panel (only when no video is playing)
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && panelVisible && !currentVideo) {
				event.preventDefault();
				handleClose();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [panelVisible, currentVideo, handleClose]);

	const handleVideoClick = useCallback(
		(video: LearningCenterVideo) => {
			trackLearningAction('video', 'open', video);
			dispatch(
				setLearningCenterState({
					currentVideo: video,
					autoplay: true,
				})
			);
		},
		[dispatch]
	);

	if (!panelVisible) return null;

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderVideoItem = (entry: LearningCenterEntry) => {
		const completed = user?.metadata.completedLearningCenterVideos?.includes(entry.id);
		const videoPosition = videoPositions[entry.id] || 0;

		// Calculate progress percentage (assuming duration is in format "M:SS")
		const durationParts = entry.duration.split(':');
		const totalSeconds = parseInt(durationParts[0]) * 60 + parseInt(durationParts[1]);
		const progressPercentage = totalSeconds > 0 ? (videoPosition / totalSeconds) * 100 : 0;

		return (
			<S.VideoItem key={entry.id} onClick={() => handleVideoClick(entry.id)}>
				<S.VideoThumbnail>
					<S.ThumbnailImage src={entry.thumbnail} alt={entry.title} />
					<S.PlayIconOverlay>
						<Icon type={IconType.PLAY} width={16} height={16} color="#ffffff" />
					</S.PlayIconOverlay>
				</S.VideoThumbnail>
				<S.VideoInfo>
					<S.VideoHeader>
						{completed && (
							<Icon
								type={IconType.CHECKBOX_CIRCLE}
								width={16}
								height={16}
								color={unthemed.green[200]}
							/>
						)}
						<S.VideoTitle>{entry.title}</S.VideoTitle>
						<S.VideoDuration>{entry.duration}</S.VideoDuration>
					</S.VideoHeader>
					<S.ProgressBar progress={progressPercentage} />
				</S.VideoInfo>
			</S.VideoItem>
		);
	};

	return (
		<S.Overlay isClosing={isClosing} onClick={handleClose}>
			<S.Panel isClosing={isClosing} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
				<S.Header>
					<S.Title>Learning Center</S.Title>
					<S.CloseButton onClick={handleClose}>
						<Icon type={IconType.CLOSE} width={20} height={20} color={theme.gray[1000]} />
					</S.CloseButton>
				</S.Header>
				<S.VideoList>{ENTRIES.map((entry) => renderVideoItem(entry))}</S.VideoList>
			</S.Panel>
		</S.Overlay>
	);
};

export default LearningCenterPanel;
